import {ChangeEvent, useState} from "react";
import {SelectField} from "@reside/ui";
import {Formik} from "formik";
import {useTemplateNamesQuery} from "./hooks/useTemplateNamesQuery";
import {Spinner} from "../../atoms/spinner";
import {AdminPageTitle} from "../../atoms/admin-layout/AdminPageTitle";
import {ButtonLink} from "../../atoms/button-link";
import {Space} from "../../atoms/space";
import {ButtonsWrapper, HubAndSpokePageHeader} from "./styles";
import {HubAndSpokeListTableScroll} from "./atoms/HubAndSpokeListTableScroll";
import {useEventTriggersQuery} from "./hooks/useEventTriggersQuery";
import {Column, ListTable} from "../../atoms/list-table";
import {EventTrigger} from "../../services/HubAndSpokeService";

const EVENT_TRIGGERS_PATH = "/admin/hub-and-spoke/event-triggers";
/** Visual minimap segments (not tied to actual column count). */
const EVENT_TRIGGERS_LIST_MINIMAP_TICKS = 6;

type Row = {
  rowData: EventTrigger;
};

export const EventTriggers = () => {
  const [templateName, setTemplateName] = useState<string>();

  const {isLoading: isLoadingTemplateNames, data: templateNames = []} =
    useTemplateNamesQuery();
  const {isLoading: isLoadingEventTriggers, data: eventTriggers = []} =
    useEventTriggersQuery({templateName});

  return (
    <>
      <HubAndSpokePageHeader>
        <AdminPageTitle>Event Triggers</AdminPageTitle>
        {isLoadingTemplateNames ? (
          <Spinner />
        ) : (
          <Space>
            <Formik initialValues={{}} onSubmit={() => undefined}>
              <SelectField
                label="Template"
                name="template-name"
                options={templateNames.map(template => ({
                  label: template,
                  value: template,
                }))}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                  const newTemplateName = event.target.value;
                  setTemplateName(newTemplateName);
                }}
              />
            </Formik>
            <ButtonLink to={`${EVENT_TRIGGERS_PATH}/create`}>
              Create new Event Trigger
            </ButtonLink>
          </Space>
        )}
      </HubAndSpokePageHeader>
      {isLoadingEventTriggers ? (
        <Spinner />
      ) : (
        <HubAndSpokeListTableScroll
          minimapTicks={EVENT_TRIGGERS_LIST_MINIMAP_TICKS}
        >
          <ListTable
            key="event-triggers"
            data={
              Array.isArray(eventTriggers) ? eventTriggers : [eventTriggers]
            }
          >
            <Column
              cellRenderer={(row: Row) => (
                <p>{row.rowData.template_field.template_name}</p>
              )}
              label="Template Name"
              sortKey="template_field.template_name"
              width={200}
            />
            <Column
              cellRenderer={(row: Row) => (
                <p>{row.rowData.template_field.friendly_name}</p>
              )}
              label="Template Field Friendly Name"
              sortKey="template_field.friendly_name"
              width={200}
            />
            <Column
              cellRenderer={(row: Row) => (
                <p>
                  {row.rowData.facilities
                    .map(facility => facility.name)
                    .join(", ")}
                </p>
              )}
              label="Facilities"
              width={200}
            />
            <Column
              cellRenderer={(row: Row) => <p>{row.rowData.sftp_config.name}</p>}
              label="SFTP Config Name"
              width={200}
            />
            <Column
              cellRenderer={(row: Row) => (
                <p>{row.rowData.sftp_config.hostname}</p>
              )}
              label="SFTP Config Hostname/IP Address"
              width={200}
            />
            <Column
              cellRenderer={(row: Row) => <p>{row.rowData.document}</p>}
              label="Document"
              sortKey="document"
              width={200}
            />
            <Column
              cellRenderer={(row: Row) => (
                <p>
                  {row.rowData.upload_on
                    .map(item => item.replace(/_/g, " "))
                    .join(", ")}
                </p>
              )}
              label="Upload On"
              sortKey="upload_on"
              width={200}
            />
            <Column
              cellRenderer={({rowData: {id}}: Row) => (
                <ButtonsWrapper>
                  <ButtonLink to={`${EVENT_TRIGGERS_PATH}/${id}`}>
                    View
                  </ButtonLink>
                  <ButtonLink
                    to={`${EVENT_TRIGGERS_PATH}/edit/${id}`}
                    color="primary"
                  >
                    Edit
                  </ButtonLink>
                </ButtonsWrapper>
              )}
              width={180}
            />
          </ListTable>
        </HubAndSpokeListTableScroll>
      )}
    </>
  );
};
